import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowDownCircle, Trophy, Coins, RefreshCw, ExternalLink, Copy, CheckCircle2, Loader2, XCircle, ArrowUpCircle, Wallet, Share2, Lock, Gift } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useWallet } from "@/lib/wallet";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";
import WithdrawModal from "@/components/WithdrawModal";
import RequiredPassportModal from "@/components/RequiredPassportModal";
import WalletsPanel from "@/components/WalletsPanel";
import RedeemCouponModal from "@/components/RedeemCouponModal";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtUsdt(cents) {
  if (cents == null) return "—";
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { hour12: false }); } catch { return iso; }
}
function shortHash(h) {
  if (!h) return null;
  if (h.length <= 16) return h;
  return `${h.slice(0, 8)}…${h.slice(-6)}`;
}

const TX_EXPLORERS = {
  TRC20:   (h) => `https://tronscan.org/#/transaction/${h}`,
  ERC20:   (h) => `https://etherscan.io/tx/${h}`,
  BEP20:   (h) => `https://bscscan.com/tx/${h}`,
  POLYGON: (h) => `https://polygonscan.com/tx/${h}`,
};

function WalletTypeBadge({ walletType }) {
  if (!walletType) return null;
  const map = {
    entry_token: { icon: Coins,  label: "ENTRY",  color: "text-cyan-300 border-cyan-400/40 bg-cyan-500/5" },
    prize:       { icon: Trophy, label: "PRIZE",  color: "text-emerald-300 border-emerald-400/40 bg-emerald-500/5" },
    bonus:       { icon: Gift,   label: "BONUS",  color: "text-fuchsia-300 border-fuchsia-400/40 bg-fuchsia-500/5" },
    locked:      { icon: Lock,   label: "LOCKED", color: "text-amber-300 border-amber-400/40 bg-amber-500/5" },
  };
  const cfg = map[walletType];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`wallet-type-badge-${walletType}`}
      className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-[1px] font-mono text-[8px] tracking-widest ${cfg.color}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {cfg.label}
    </span>
  );
}


function TypeBadge({ type, status }) {
  const map = {
    deposit:      { icon: ArrowDownCircle, label: "DEPÓSITO",     color: "text-cyan-300 border-cyan-400/40 bg-cyan-500/5" },
    prize:        { icon: Trophy,          label: "PRÊMIO",       color: "text-emerald-300 border-emerald-400/40 bg-emerald-500/5" },
    withdraw:     { icon: ArrowUpCircle,   label: "SAQUE",        color: "text-amber-300 border-amber-400/40 bg-amber-500/5" },
    entry_fee:    { icon: ArrowUpCircle,   label: "TAXA",         color: "text-orange-300 border-orange-400/40 bg-orange-500/5" },
    refund:       { icon: RefreshCw,       label: "REEMBOLSO",    color: "text-blue-300 border-blue-400/40 bg-blue-500/5" },
    admin_credit: { icon: Coins,           label: "CRÉDITO",      color: "text-primary border-primary/40 bg-primary/5" },
  };
  const cfg = map[type] || map.entry_fee;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-[3px] font-mono text-[9px] tracking-widest ${cfg.color}`}>
      <Icon className="h-3 w-3" />{cfg.label}
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    completed: { icon: CheckCircle2, label: "CONFIRMADO", color: "text-emerald-300 border-emerald-400/40" },
    pending:   { icon: Loader2,      label: "PENDENTE",   color: "text-yellow-300 border-yellow-400/40 animate-pulse" },
    failed:    { icon: XCircle,      label: "FALHOU",     color: "text-red-300 border-red-400/40" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-[1px] font-mono text-[8px] tracking-widest ${cfg.color}`}>
      <Icon className={`h-2.5 w-2.5 ${status === "pending" ? "animate-spin" : ""}`} />{cfg.label}
    </span>
  );
}

function HashCell({ hash, network }) {
  const [copied, setCopied] = useState(false);
  if (!hash) return <span className="text-muted-foreground/50 font-mono text-[10px]">—</span>;
  const url = network && TX_EXPLORERS[network] ? TX_EXPLORERS[network](hash) : null;
  return (
    <div className="inline-flex items-center gap-1.5">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:text-primary/80 font-mono text-[10px] underline-offset-4 hover:underline inline-flex items-center gap-1"
           data-testid={`tx-hash-link-${hash.slice(0, 6)}`}>
          {shortHash(hash)}<ExternalLink className="h-2.5 w-2.5" />
        </a>
      ) : (
        <span className="font-mono text-[10px] text-primary">{shortHash(hash)}</span>
      )}
      <button
        onClick={async () => { try { await navigator.clipboard.writeText(hash); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch (e) { /* noop */ } }}
        className="text-muted-foreground hover:text-primary transition"
        data-testid={`tx-hash-copy-${hash.slice(0, 6)}`}
      >
        {copied ? <CheckCircle2 className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
}

export default function Wallet_() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { tickets } = useWallet(15000);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState(null);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showRedeem, setShowRedeem] = useState(false);
  const [walletsRefreshKey, setWalletsRefreshKey] = useState(0);

  const fetchHistory = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/wallet/me/history?limit=200`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) { navigate("/"); return; }
    if (user) fetchHistory();
  }, [user, authLoading, navigate, fetchHistory]);

  const filtered = (data?.entries || []).filter((e) => {
    if (filter === "all") return true;
    if (filter === "deposits") return e.type === "deposit";
    if (filter === "prizes") return e.type === "prize";
    if (filter === "withdrawals") return e.type === "withdraw";
    if (filter === "movements") return e.type === "entry_fee" || e.type === "refund" || e.type === "admin_credit";
    return true;
  });

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background grid place-items-center text-primary font-mono text-xs tracking-widest">
        {t("wallet.loading")}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_#050a18_0%,_#020408_60%,_#000_100%)] text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-primary/20 bg-black/70 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
          <button onClick={() => navigate("/pilot")} data-testid="wallet-back-btn"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-primary/30 px-2 sm:px-3 py-1.5 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest">
            <ArrowLeft className="h-3 w-3" /> <span className="hidden sm:inline">{t("wallet.back")}</span>
          </button>
          <div className="font-mono text-[9px] tracking-[0.3em] text-muted-foreground hidden md:block">{t("wallet.transactions_label")}</div>
          <div className="flex items-center gap-2">
            <button onClick={fetchHistory} data-testid="wallet-refresh-btn"
                    className="inline-flex items-center gap-1.5 rounded-sm border border-primary/30 px-2 sm:px-3 py-1.5 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">{t("wallet.refresh")}</span>
            </button>
            <LanguageSelector />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-4 pt-6 sm:pt-8 space-y-6 sm:space-y-8">
        {/* Phase-2 4-wallet panel — self-contained, no callback to avoid render loop */}
        <WalletsPanel key={walletsRefreshKey} />

        {/* Redeem coupon CTA */}
        <div className="rounded-sm border border-fuchsia-400/40 bg-fuchsia-500/5 p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-fuchsia-300" />
            <p className="font-mono text-[10px] text-fuchsia-200 tracking-widest">
              TEM UM CÓDIGO PROMOCIONAL OU DE INDICAÇÃO?
            </p>
          </div>
          <button onClick={() => setShowRedeem(true)}
                  data-testid="wallet-open-redeem"
                  className="rounded-sm bg-fuchsia-500 text-black hover:bg-fuchsia-400 font-mono tracking-widest text-[10px] px-4 py-2">
            RESGATAR
          </button>
        </div>

        {/* Withdraw action + tickets summary */}
        <div className="rounded-sm border border-primary/40 bg-black/40 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
             data-testid="wallet-balance-card">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.3em] text-primary/70">{t("wallet.balance_label")}</div>
            <div className="flex flex-wrap items-baseline gap-3 mt-1">
              <span className="font-display font-black text-2xl sm:text-3xl text-white tracking-tighter">
                {fmtUsdt(data?.balance_cents ?? 0)} <span className="text-primary text-base">USDT</span>
              </span>
              <span className="text-white/30">·</span>
              <span data-testid="wallet-tickets" className="text-amber-200 inline-flex items-center gap-1 font-display font-black text-2xl sm:text-3xl tracking-tighter">
                {tickets ?? 0} <span className="text-amber-200 text-xl sm:text-2xl">🎟</span>
              </span>
            </div>
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              {t(`wallet.transactions_count_${(data?.entries?.length ?? 0) === 1 ? "one" : "other"}`, { count: data?.entries?.length ?? 0 })}
              {" · "}
              {t(`wallet.tickets_count_${(tickets ?? 0) === 1 ? "one" : "other"}`, { count: tickets ?? 0 })}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowWithdraw(true)}
              data-testid="wallet-withdraw-btn"
              disabled={(data?.balance_cents ?? 0) < 500}
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed font-mono tracking-widest text-[11px] px-4 py-3 transition"
            >
              <ArrowUpCircle className="h-4 w-4" /> SACAR PRÊMIOS
            </button>
            <Wallet className="h-12 w-12 text-primary/40 shrink-0 hidden md:block" />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: "all",         label: t("wallet.filters.all") },
            { id: "deposits",    label: t("wallet.filters.deposits") },
            { id: "prizes",      label: t("wallet.filters.prizes") },
            { id: "withdrawals", label: "SAQUES" },
            { id: "movements",   label: t("wallet.filters.movements") },
          ].map((tb) => (
            <button key={tb.id}
                    onClick={() => setFilter(tb.id)}
                    data-testid={`wallet-filter-${tb.id}`}
                    className={`px-3 py-1.5 rounded-sm border font-mono text-[10px] tracking-widest transition ${
                      filter === tb.id
                        ? "border-primary bg-primary text-black"
                        : "border-primary/30 text-primary hover:bg-primary/10"
                    }`}>
              {tb.label}
            </button>
          ))}
        </div>

        <>
        {/* Entries */}
        {err ? (
          <div className="rounded-sm border border-red-400/40 bg-red-500/5 p-4 font-mono text-xs text-red-300"
               data-testid="wallet-error">
            {t("wallet.error", { msg: err })}
          </div>
        ) : loading ? (
          <div className="text-center py-16 text-muted-foreground font-mono text-xs tracking-widest">{t("wallet.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground font-mono text-xs tracking-widest border border-dashed border-primary/20 rounded-sm"
               data-testid="wallet-empty-state">
            {t("wallet.empty")}
          </div>
        ) : (
          <div className="space-y-2" data-testid="wallet-history-list">
            {filtered.map((e, i) => {
              const amount = e.amount_cents ?? 0;
              const isCredit = amount > 0;
              return (
                <div key={`${e.type}-${e.ref_id}-${e.date}-${i}`}
                     data-testid={`wallet-entry-${e.type}`}
                     className="rounded-sm border border-primary/15 bg-black/40 hover:border-primary/30 transition p-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto] gap-3 md:gap-4 items-center">
                  <div className="flex items-center gap-2 min-w-[130px]">
                    <TypeBadge type={e.type} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{e.title}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                      <span className="font-mono text-[10px] text-muted-foreground">{fmtDate(e.date)}</span>
                      <StatusPill status={e.status} />
                      <WalletTypeBadge walletType={e.wallet_type} />
                      {e.network && <span className="font-mono text-[9px] tracking-widest text-cyan-400/70">{e.network}</span>}
                      {e.rank != null && <span className="font-mono text-[9px] tracking-widest text-yellow-300">{e.rank}º LUGAR</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-display font-black text-lg tracking-tighter ${
                      isCredit ? "text-emerald-300" : amount < 0 ? "text-orange-300" : "text-muted-foreground"
                    }`}>
                      {amount > 0 ? "+" : ""}{fmtUsdt(amount)}
                    </div>
                    {e.status === "pending" && e.expected_cents > 0 && e.type === "deposit" && (
                      <div className="font-mono text-[9px] text-yellow-300/80">esperado: {fmtUsdt(e.expected_cents)}</div>
                    )}
                  </div>
                  <div className="md:min-w-[180px] md:text-right flex items-center gap-2 md:justify-end">
                    <HashCell hash={e.tx_hash} network={e.network} />
                    {e.type === "prize" && e.rank != null && e.rank <= 10 && (
                      <button
                        onClick={() => {
                          const amount = (e.amount_cents / 100).toFixed(2);
                          const text = `Fui TOP ${e.rank} num torneio da $UFO Coin e levei $${amount} USDT 🛸🏆%0A%0AEntra na frota também → https://x.com/UFOCoinSOL`;
                          window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
                        }}
                        data-testid={`share-victory-${e.rank}`}
                        title="Compartilhar vitória no X"
                        className="p-1.5 rounded-sm border border-amber-300/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 transition-colors">
                        <Share2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
      </div>

      <WithdrawModal
        open={showWithdraw}
        balanceCents={data?.balance_cents ?? 0}
        onClose={() => setShowWithdraw(false)}
        onCreated={() => { setShowWithdraw(false); fetchHistory(); setWalletsRefreshKey(k => k + 1); }}
      />
      <RedeemCouponModal
        open={showRedeem}
        onClose={() => setShowRedeem(false)}
        onSuccess={() => { fetchHistory(); setWalletsRefreshKey(k => k + 1); }}
      />
      <RequiredPassportModal user={user} />
    </div>
  );
}
